import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Check, X, Search, FileText, Plus, Trash2, Lock } from "lucide-react";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import EstoqueReportModal from "../../components/EstoqueReportModal";

const CATEGORIAS = ["Óleo", "Filtro", "Aditivo", "Fluido", "Estética", "Acessório", "Geral"];

export default function EstoqueMaster() {
  const [unidades, setUnidades] = useState([]);
  const [scope, setScope] = useState("global");
  const [estoque, setEstoque] = useState([]);
  const [busca, setBusca] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [reportOpen, setReportOpen] = useState(false);

  // Modal produto (novo / editar)
  const [produtoModal, setProdutoModal] = useState(null);
  const [produtoForm, setProdutoForm] = useState({
    nome: "",
    categoria: "Óleo",
  });

  // Modal confirmar exclusão de produto
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
  }, []);

  const load = async () => {
    const params = scope === "global" ? {} : { unidade_id: scope };
    const r = await api.get("/estoque", { params });
    setEstoque(r.data);
  };

  useEffect(() => {
    load();
  }, [scope]); // eslint-disable-line

  const filtrados = estoque.filter((e) => {
    const txt = `${e.produto_nome} ${e.categoria}`.toLowerCase();
    if (busca && !txt.includes(busca.toLowerCase())) return false;
    if (categoriaFiltro && e.categoria !== categoriaFiltro) return false;
    return true;
  });

  const startEdit = (row) => {
    setEditRow(row.id);
    setEditVals({ preco_venda: row.preco_venda });
  };

  const saveEdit = async () => {
    await api.put(`/estoque/${editRow}`, editVals);
    toast.success("Preço de venda atualizado");
    setEditRow(null);
    load();
  };

  const abrirNovoProduto = () => {
    setProdutoForm({ nome: "", categoria: "Óleo" });
    setProdutoModal({ mode: "new" });
  };

  const abrirEditarProduto = (row) => {
    setProdutoForm({
      nome: row.produto_nome,
      categoria: row.categoria,
    });
    setProdutoModal({ mode: "edit", produtoId: row.produto_id, produtoNome: row.produto_nome });
  };

  const salvarProduto = async () => {
    if (!produtoForm.nome.trim()) {
      toast.error("Informe o nome do produto");
      return;
    }
    try {
      if (produtoModal.mode === "new") {
        await api.post("/produtos", { ...produtoForm, preco_custo_global: 0 });
        toast.success("Produto criado. Registre uma ENTRADA para iniciar o custo médio.");
      } else {
        await api.put(`/produtos/${produtoModal.produtoId}`, produtoForm);
        toast.success("Produto atualizado");
      }
      setProdutoModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar produto");
    }
  };

  const excluirProduto = async () => {
    try {
      await api.delete(`/produtos/${confirmDelete.produto_id}`);
      toast.success("Produto e estoques vinculados excluídos");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  // Categorias existentes no dataset atual (para filtro dinâmico)
  const categoriasDisponiveis = Array.from(
    new Set(estoque.map((e) => e.categoria).filter(Boolean)),
  ).sort();

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Catálogo e gestão por unidade
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Produtos & Estoque
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="produto-novo-btn"
            onClick={abrirNovoProduto}
            className="bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
          >
            <Plus className="w-4 h-4" /> Novo produto
          </button>
          <button
            data-testid="estoque-relatorio-btn"
            onClick={() => setReportOpen(true)}
            className="bg-yellow-400 hover:bg-yellow-500 text-neutral-950 h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
          >
            <FileText className="w-4 h-4" /> Relatório · CSV · Térmico
          </button>
        </div>
      </div>

      <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />

      <div className="bg-white border-2 border-neutral-900">
        <div className="p-3 border-b-2 border-neutral-900 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="estoque-busca"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar produto ou categoria..."
              className="w-full h-10 pl-8 pr-3 font-mono text-sm focus:outline-none border-2 border-transparent focus:border-neutral-900"
            />
          </div>
          <select
            data-testid="estoque-categoria-filtro"
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
            className="h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
          >
            <option value="">Todas categorias</option>
            {categoriasDisponiveis.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-300 bg-neutral-50">
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                  Produto
                </th>
                {scope === "global" && (
                  <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">
                    Unidade
                  </th>
                )}
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">
                  Categoria
                </th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                  Custo médio
                </th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                  Preço Venda
                </th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">
                  Qtd
                </th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((e) => (
                <tr key={e.id} className="border-b border-neutral-100">
                  <td className="p-3 font-display font-bold">{e.produto_nome}</td>
                  {scope === "global" && (
                    <td className="p-3 text-xs">
                      <span
                        className="inline-block w-2 h-2 mr-1 align-middle"
                        style={{
                          backgroundColor:
                            unidades.find((u) => u.id === e.unidade_id)?.slug ===
                            "santa_clara"
                              ? "#2563EB"
                              : "#DC2626",
                        }}
                      />
                      {unidades.find((u) => u.id === e.unidade_id)?.nome || "—"}
                    </td>
                  )}
                  <td className="p-3 hidden md:table-cell">
                    <span className="bg-neutral-100 px-2 py-0.5 font-mono text-xs uppercase">
                      {e.categoria}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono text-sm">
                    <span className="inline-flex items-center gap-1 text-neutral-700">
                      <Lock className="w-3 h-3 text-neutral-400" />
                      R$ {Number(e.preco_custo).toFixed(2).replace(".", ",")}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono font-bold">
                    {editRow === e.id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editVals.preco_venda}
                        onChange={(ev) =>
                          setEditVals({ ...editVals, preco_venda: Number(ev.target.value) })
                        }
                        className="w-24 h-9 px-2 border-2 border-neutral-900 font-mono text-sm text-right"
                      />
                    ) : (
                      `R$ ${Number(e.preco_venda).toFixed(2).replace(".", ",")}`
                    )}
                  </td>
                  <td className="p-3 text-right font-mono font-bold">
                    <span
                      className={`inline-flex items-center gap-1 ${
                        e.quantidade_atual < 0 ? "text-red-600" : "text-neutral-900"
                      }`}
                    >
                      <Lock className="w-3 h-3 text-neutral-400" />
                      {e.quantidade_atual}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {editRow === e.id ? (
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`estoque-save-${e.id}`}
                          onClick={saveEdit}
                          className="p-2 bg-green-600 text-white hover:bg-green-700"
                          title="Salvar preço de venda"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditRow(null)}
                          className="p-2 border border-neutral-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`estoque-editar-${e.id}`}
                          onClick={() => startEdit(e)}
                          className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Editar preço de venda"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`produto-editar-${e.produto_id}`}
                          onClick={() => abrirEditarProduto(e)}
                          className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          title="Editar produto (nome, categoria)"
                        >
                          <span className="font-mono text-[10px] font-bold">P</span>
                        </button>
                        <button
                          data-testid={`produto-excluir-${e.produto_id}`}
                          onClick={() =>
                            setConfirmDelete({
                              produto_id: e.produto_id,
                              nome: e.produto_nome,
                            })
                          }
                          className="p-2 border border-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600"
                          title="Excluir produto (e estoques vinculados)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td
                    colSpan={scope === "global" ? 7 : 6}
                    className="p-8 text-center text-sm text-neutral-500"
                  >
                    Nenhum item
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-2 border-t border-neutral-200 bg-neutral-50 font-mono text-[10px] text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>
            <Pencil className="w-3 h-3 inline mr-1" /> Editar preço de venda
          </span>
          <span>
            <span className="font-mono text-[9px] font-bold border border-neutral-500 px-1">P</span>{" "}
            Editar produto (nome, categoria)
          </span>
          <span>
            <Trash2 className="w-3 h-3 inline mr-1" /> Excluir produto
          </span>
          <span className="ml-auto">
            <Lock className="w-3 h-3 inline mr-1" /> Custo e quantidade só mudam via
            Movimentações (auditável)
          </span>
        </div>
      </div>

      <EstoqueReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        titulo={
          scope === "global"
            ? "Estoque Total (Global)"
            : `Estoque — ${unidades.find((u) => u.id === scope)?.nome || ""}`
        }
        escopo={
          scope === "global"
            ? "GLOBAL (todas as unidades)"
            : unidades.find((u) => u.id === scope)?.nome || ""
        }
        itens={filtrados}
        unidades={unidades}
        scope={scope}
      />

      {/* Modal Novo / Editar Produto */}
      {produtoModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900">
              <h3 className="font-display font-bold uppercase">
                {produtoModal.mode === "new" ? "Novo Produto" : "Editar Produto"}
              </h3>
              <button onClick={() => setProdutoModal(null)} className="p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <L label="Nome">
                <input
                  data-testid="produto-form-nome"
                  value={produtoForm.nome}
                  onChange={(e) => setProdutoForm({ ...produtoForm, nome: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
                />
              </L>
              <L label="Categoria">
                <select
                  data-testid="produto-form-categoria"
                  value={produtoForm.categoria}
                  onChange={(e) =>
                    setProdutoForm({ ...produtoForm, categoria: e.target.value })
                  }
                  className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono bg-white"
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </L>
              <div className="text-xs text-neutral-600 bg-yellow-50 border-l-4 border-yellow-500 p-3">
                <div className="flex items-center gap-1 font-bold uppercase tracking-widest text-[10px] mb-1">
                  <Lock className="w-3 h-3" /> Preço de custo agora é calculado
                </div>
                {produtoModal.mode === "new"
                  ? "O produto será criado em TODAS as unidades com estoque zero. Depois use ‘Movimentações → Entrada’ para lançar a primeira compra: o sistema calcula o custo médio ponderado automaticamente."
                  : "O custo médio é calculado a cada Entrada de estoque (custo médio ponderado por unidade). Ajustes de quantidade/perda são feitos em ‘Movimentações → Saída’."}
              </div>
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex gap-2 justify-end">
              <button
                onClick={() => setProdutoModal(null)}
                className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
              >
                Cancelar
              </button>
              <button
                data-testid="produto-form-salvar"
                onClick={salvarProduto}
                className="bg-neutral-950 hover:bg-neutral-800 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest cta-brutal"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar exclusão de produto */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-red-600 w-full max-w-md">
            <div className="p-4 border-b-2 border-neutral-900 bg-red-600 text-white">
              <h3 className="font-display font-black uppercase">Excluir produto?</h3>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-sm">
                Você está prestes a excluir:{" "}
                <span className="font-display font-bold">{confirmDelete.nome}</span>
              </p>
              <p className="text-xs text-neutral-600">
                Essa ação remove o produto do catálogo e apaga TODOS os estoques vinculados
                (em todas as unidades). Vendas passadas permanecem no histórico.
              </p>
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
              >
                Cancelar
              </button>
              <button
                data-testid="produto-excluir-confirmar"
                onClick={excluirProduto}
                className="bg-red-600 hover:bg-red-700 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest cta-brutal"
              >
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
      {label}
    </span>
    {children}
  </label>
);
